import React, { useState, useRef, useEffect } from "react";
import { ClockIcon } from "@heroicons/react/24/outline";

const OTP_LENGTH = 4;

const OtpInput = ({ phone, handleResend, onLogin }) => {
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
    const [countdown, setCountdown] = useState(59);
    const [canResend, setCanResend] = useState(false);
    const inputRefs = useRef([]);

    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    useEffect(() => {
        if (canResend) return;

        if (countdown <= 0) {
            setCanResend(true);
            return;
        }

        const t = setTimeout(() => {
            setCountdown((c) => c - 1);
        }, 1000);

        return () => clearTimeout(t);
    }, [countdown, canResend]);

    const handleSubmitResend = () => {
        if (!canResend) return;
        handleResend();
        setOtp(Array(OTP_LENGTH).fill(""));
        setCountdown(59);
        setCanResend(false);
        inputRefs.current[0]?.focus();
    };

    const handleChange = (idx, val) => {
        const digit = val.replace(/\D/g, "").slice(-1);
        const next = [...otp];
        next[idx] = digit;
        setOtp(next);
        if (digit && idx < OTP_LENGTH - 1) {
            inputRefs.current[idx + 1]?.focus();
        }
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === "Backspace" && !otp[idx] && idx > 0) {
            inputRefs.current[idx - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
        const next = [...otp];
        pasted.split("").forEach((char, i) => { next[i] = char; });
        setOtp(next);
        inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    };

    const isComplete = otp.every((d) => d !== "");
    const formattedPhone = `+62${phone}`;

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;

        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Masukkan OTP</h2>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                Kami baru saja mengirimkan SMS kepada Anda, masukkan kode OTP 4 digit.
            </p>

            <div className="flex justify-center gap-3 mb-3" onPaste={handlePaste}>
                {otp.map((digit, idx) => (
                    <input
                        key={idx}
                        ref={(el) => (inputRefs.current[idx] = el)}
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(idx, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(idx, e)}
                        className={`w-20 h-12 text-center text-xl font-semibold rounded-md border-2 outline-none transition-all
                            ${digit
                                ? "border-[var(--rose-violet)] bg-white text-gray-900"
                                : idx === otp.findIndex((d) => d === "")
                                    ? "border-gray-400 bg-white"
                                    : "border-gray-200 bg-gray-100 text-gray-400"
                            }`}
                        style={{ caretColor: "transparent" }}
                    />
                ))}
            </div>

            <div className="flex justify-between">
                <div className="flex items-center gap-1 text-left mb-6">
                    <ClockIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-400 pl-2">{formatTime(countdown)}</span>
                </div>

                <div className="text-right mb-6">
                    <button
                        onClick={handleSubmitResend}
                        className={`text-sm font-semibold ${canResend
                            ? "text-[var(--rose-violet)] cursor-pointer"
                            : "text-gray-300 cursor-not-allowed"
                            } bg-transparent border-0`}
                        disabled={!canResend}
                    >
                        Resend code
                    </button>
                </div>
            </div>

            <button
                onClick={() => isComplete && onLogin(otp.join(""))}
                disabled={!isComplete}
                className={`w-full py-4 rounded-full font-semibold text-sm transition-all border-0
                    ${isComplete
                        ? "bg-[var(--rose-violet)] text-white cursor-pointer"
                        : "bg-gray-100 text-gray-300 cursor-not-allowed"
                    }`}
            >
                Login
            </button>
        </>
    );
};

export default OtpInput;